/**
 * Chat Service
 * Handles chat operations (send messages, streaming)
 */

import { CHATBOT_API_BASE_URL } from '../utils/apiConfig';
import { createHeaders } from '../utils/authHelper';

export const chatService = {
    /**
     * Send message to chatbot
     * @param {string} message - User message
     * @param {string|null} questionId - Optional question ID for context
     * @param {string} modelChoice - OpenAI model to use
     * @param {boolean} answerConfirmed - Whether user has confirmed their answer
     * @param {string} language - Language setting (en or vi)
     * @returns {Promise<Object>} Chat response
     */
    async sendMessage(
        message,
        questionId = null,
        modelChoice = "gpt-4o-mini",
        answerConfirmed = false,
        language = "vi"
    ) {
        try {
            if (!message || !message.trim()) {
                throw new Error("Message cannot be empty");
            }

            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/chat`,
                {
                    method: "POST",
                    headers: createHeaders(),
                    body: JSON.stringify({
                        question: message.trim(),
                        question_id: questionId,
                        model_choice: modelChoice,
                        answer_confirmed: answerConfirmed,
                        language: language,
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Chat failed: HTTP ${response.status} - ${errorText}`
                );
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error("❌ Error sending message:", error);
            throw error;
        }
    },

    /**
     * Send message with streaming response support
     * @param {string} message - User message
     * @param {string|null} questionId - Optional question ID
     * @param {Function} onToken - Callback for each token received
     * @param {Function} onError - Callback for errors
     * @param {Object} options - Additional options (modelChoice, answerConfirmed, signal, session_id)
     */
    async sendMessageStream(
        message,
        questionId = null,
        onToken,
        onError,
        options = {}
    ) {
        try {
            if (!message || !message.trim()) {
                throw new Error("Message cannot be empty");
            }

            const requestBody = {
                question: message.trim(),
                question_id: questionId,
                model_choice: options.modelChoice || "gpt-4o-mini",
                answer_confirmed: options.answerConfirmed || false,
                session_id: options.session_id, // Pass session_id for custom prompts
                language: options.language || "vi", // Language setting (en or vi)
                stream: true,
            };

            // DEBUG: Log request payload
            console.log("📡 [chatService] Sending request to API:");
            console.log("📡 [chatService] URL:", `${CHATBOT_API_BASE_URL}/api/chat`);
            console.log("📡 [chatService] Request body:", requestBody);
            console.log("📡 [chatService] answer_confirmed value:", requestBody.answer_confirmed);

            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/chat`,
                {
                    method: "POST",
                    headers: createHeaders(),
                    body: JSON.stringify(requestBody),
                    signal: options.signal, // Support abort signal for canceling
                }
            );

            if (!response.ok) {
                // Fallback to regular message
                const result = await this.sendMessage(
                    message,
                    questionId,
                    options.modelChoice || "gpt-4o-mini",
                    options.answerConfirmed || false,
                    options.language || "vi"
                );
                if (result.response || result.answer) {
                    // Simulate streaming for consistent UX
                    const text = result.response || result.answer;
                    const words = text.split(" ");

                    for (let i = 0; i < words.length; i++) {
                        setTimeout(() => {
                            onToken(
                                words[i] +
                                (i < words.length - 1 ? " " : "")
                            );
                        }, i * 50);
                    }
                } else {
                    onToken(JSON.stringify(result));
                }
                return;
            }

            // Check if response is streaming
            const contentType = response.headers.get("content-type");
            if (
                contentType &&
                (contentType.includes("text/stream") ||
                    contentType.includes("text/event-stream"))
            ) {
                // Handle streaming response
                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value);
                        const lines = chunk.split("\n");

                        for (const line of lines) {
                            if (line.trim()) {
                                if (line.startsWith("data: ")) {
                                    try {
                                        const data = JSON.parse(
                                            line.substring(6)
                                        );

                                        // Handle new SSE format with type field
                                        if (data.type === 'content') {
                                            // Stream text content progressively
                                            onToken(data.content || '');
                                        } else if (data.type === 'done') {
                                            // Stream completed - log metadata
                                            console.log('✅ Stream complete:', data);
                                        } else if (data.type === 'error') {
                                            // Error occurred
                                            console.error('❌ Stream error:', data.error);
                                            if (onError) {
                                                onError(new Error(data.error));
                                            }
                                        } else if (
                                            data.token ||
                                            data.content ||
                                            data.text
                                        ) {
                                            // Fallback for old format (backward compatibility)
                                            onToken(
                                                data.token ||
                                                data.content ||
                                                data.text
                                            );
                                        }
                                    } catch (e) {
                                        console.warn(
                                            "Failed to parse streaming data:",
                                            e
                                        );
                                        onToken(line.substring(6));
                                    }
                                } else {
                                    onToken(line);
                                }
                            }
                        }
                    }
                } finally {
                    reader.releaseLock();
                }
            } else {
                // Handle regular JSON response
                const result = await response.json();
                if (result.response || result.answer) {
                    onToken(result.response || result.answer);
                } else {
                    onToken(JSON.stringify(result));
                }
            }
        } catch (error) {
            // Check if this was an abort (user clicked Stop)
            if (error.name === 'AbortError') {
                console.log("🛑 Streaming was cancelled by user");
                return; // Don't treat as error, just stop
            }

            console.error("Error in streaming chat:", error);
            if (onError) {
                onError(error);
            } else {
                // Fallback: try regular message
                try {
                    const result = await this.sendMessage(
                        message,
                        questionId,
                        options.modelChoice || "gpt-4o-mini",
                        options.answerConfirmed || false,
                        options.language || "vi"
                    );
                    onToken(
                        result.response ||
                        result.answer ||
                        JSON.stringify(result)
                    );
                } catch (fallbackError) {
                    throw fallbackError;
                }
            }
        }
    },
};
